import {Moment} from 'moment';
import {EntityInfo} from '../../../../../backend/interfaces/entity-info';
import {EntityOccurrence} from '../../../../../backend/responses/entity-occurrence';
import {EntityReportsRepository} from '../../../../../repositories/entity-reports';
import {getOccurrenceStatus} from '../../../occurrences';

export type ExportOccurrencesReportData = {
  reportData: any[];
};

export async function exportOccurencesGetReportData({
  entity,
  filters,
}: {
  entity?: EntityInfo;
  filters: {
    user?: string;
    startDate: Moment;
    endDate: Moment;
    view: string;
    socialResponses?: string[];
    status?: Array<'registered' | 'handled' | 'not_handled'>;
    patientOccurrences?: string[];
    generalOccurrences?: string[];
  };
}): Promise<ExportOccurrencesReportData | null> {
  if (!entity?.id) {
    return null;
  }

  const reportFilters = createOccurencesFilters(filters);

  const reportData = await EntityReportsRepository.instance.occurrences({
    entityId: entity.id,
    sort: 'registeredAt DESC',
    body: {
      targetUserIds: reportFilters.preFlight.targetUserIds,
      typeIds: reportFilters.preFlight.typeIds,
      entitySocialResponseIds: reportFilters.preFlight.socialResponses,
      registeredAt: {
        from: filters.startDate.toISOString(),
        to: filters.endDate.toISOString(),
      },
      propertiesToInclude: [
        'typeId',
        'registeredAt',
        'actionRequired',
        'markedAsHandled',
        'actionRequired',
        'acknowledgements',
        'targetUserId',
        'responsibleUserId',
        'markedAsHandledByUserId',
        'notes',
        'details',
        'siteName',
      ],
      registeredByUser: {
        propertiesToInclude: ['name', 'displayName'],
      },
      targetUser: {
        propertiesToInclude: ['name', 'displayName'],
      },
      responsibleUser: {
        propertiesToInclude: ['name', 'displayName'],
      },
      markedAsHandledByUser: {
        propertiesToInclude: ['name', 'displayName'],
      },
      type: {
        propertiesToInclude: ['name'],
      },
    },
  });

  return {
    reportData: filterReportData(reportData, reportFilters.postFlight),
  };
}

function createOccurencesFilters(filters: {
  user?: string;
  startDate: Moment;
  endDate: Moment;
  view: string;
  socialResponses?: string[];
  status?: Array<'registered' | 'handled' | 'not_handled'>;
  patientOccurrences?: string[];
  generalOccurrences?: string[];
}) {
  let typesIds: string[] | undefined = [
    ...(filters.patientOccurrences ?? []),
    ...(filters.generalOccurrences ?? []),
  ];
  if (typesIds.length === 0) {
    typesIds = undefined;
  }

  return {
    preFlight: {
      socialResponses: filters.socialResponses,
      targetUserIds: filters.user ? [filters.user] : undefined,
      typeIds: typesIds,
    },
    postFlight: {
      status: {
        not_handled: filters.status?.includes('not_handled') ? true : undefined,
        registered: filters.status?.includes('registered') ? true : undefined,
        handled: filters.status?.includes('handled') ? true : undefined,
      },
    },
  };
}

function filterReportData(
  reportData: any[],
  filters: {
    status: {
      not_handled?: boolean;
      registered?: boolean;
      handled?: boolean;
    };
  }
) {
  const result: any[] = [];

  for (const occurrence of reportData) {
    const status = getOccurrenceStatus(occurrence as EntityOccurrence);

    if (filters.status.not_handled && status === 'not_handled') {
      result.push(occurrence);
      continue;
    }

    if (filters.status.registered && status === 'registered') {
      result.push(occurrence);
      continue;
    }

    if (filters.status.handled && status === 'handled') {
      result.push(occurrence);
      continue;
    }
  }

  return result.length > 0 ? result : reportData;
}
